import apiClient from './client';

export interface AgreementClause {
  enabled: boolean;
  content: string;
}

export interface AgreementClauses {
  // Standard — both agreement types
  change_orders:        AgreementClause;
  warranty:             AgreementClause;
  lien_rights:          AgreementClause;
  confidentiality:      AgreementClause;
  termination:          AgreementClause;
  dispute:              AgreementClause;
  general_conditions:   AgreementClause;
  // WO-specific
  schedule_time:           AgreementClause;
  independent_contractor:  AgreementClause;
  wcb_ohs:                 AgreementClause;
  insurance_requirements:  AgreementClause;
  // Open field
  additional:           AgreementClause;
}

export interface PaymentMilestone {
  description: string;
  amount: number | null;
  percent: number | null;
  due_when: string;
}

export interface MaterialsSupply {
  labour_only: boolean;
  materials_by_principal: boolean;
  materials_by_subcontractor: boolean;
  equipment_by_principal: boolean;
  equipment_by_subcontractor: boolean;
}

// ── Shared clause text (used in both WA and WO defaults) ─────────────────────

export const CLAUSE_TEXT = {
  change_orders:
`No change, addition, deletion, or substitution to the Scope of Work is valid unless agreed in writing and signed by both parties on a Change Order before the change is performed.

The Contractor shall not perform unauthorized extra work and shall not claim compensation for it.

OchrePoint may direct minor extra work valued under $500 verbally; such direction must be confirmed in writing within 48 hours to form a binding Change Order.`,

  warranty:
`The Contractor warrants all work against defects in materials and workmanship for one (1) year from the date of Substantial Completion, or any longer period required by law.

Upon written notice of a deficiency, the Contractor shall remedy it at no cost to OchrePoint within 10 business days, or sooner if circumstances reasonably require.

All work shall conform to the current BC Building Code, applicable trade standards, and manufacturer installation requirements.`,

  lien_rights:
`The Contractor acknowledges and agrees to comply fully with the Builders Lien Act, RSBC 1996, c. 41, including all obligations relating to holdback, lien rights, and statutory declarations.

The Contractor shall provide OchrePoint, upon request, with statutory declarations confirming all sub-trades, suppliers, and workers have been paid in full to the declaration date.

If any party engaged by the Contractor files a lien against the project, OchrePoint may withhold from amounts owing an amount sufficient to discharge such lien(s) plus reasonable costs.

The Contractor shall discharge any lien filed by parties it engaged within 30 days of written notice from OchrePoint.`,

  confidentiality:
`The Contractor shall keep confidential all client information, project details, pricing, and proprietary business information of OchrePoint and its clients, and shall not disclose such to any third party without prior written consent.

The Contractor shall not photograph, post to social media, or otherwise publish images of the site, completed work, or clients without the express written consent of OchrePoint.

These obligations survive termination for two (2) years.`,

  termination:
`For Convenience: OchrePoint may terminate on 5 business days' written notice. The Contractor is entitled to payment for work satisfactorily completed and accepted to the termination date, less holdback and any amounts owing to OchrePoint.

For Cause: OchrePoint may terminate immediately if the Contractor (a) materially breaches and fails to cure within 48 hours of written notice; (b) becomes insolvent; (c) abandons the work; or (d) violates applicable law or safety regulations. The Contractor forfeits all right to further payment; OchrePoint may engage others to complete the work and charge any excess cost to the Contractor.

Upon termination for any reason, the Contractor shall promptly remove its equipment and materials and leave the site clean and safe.`,

  dispute:
`Negotiation: The parties shall attempt in good faith to resolve disputes within 10 business days of written notice identifying the dispute.

Mediation: If negotiation fails, either party may refer the matter to non-binding mediation through the BC Mediation Roster Society. Costs are shared equally.

Arbitration / Courts: Unresolved disputes may proceed under the Arbitration Act, RSBC 2020, c. 2, or through the courts of British Columbia; venue: Cowichan Valley / Duncan, BC. The prevailing party may recover reasonable legal costs.`,

  general_conditions:
`Governing Law: This agreement is governed by the laws of British Columbia and the laws of Canada applicable therein.

Entire Agreement: This agreement, together with any attached Schedules and signed Change Orders, constitutes the entire agreement and supersedes all prior negotiations, representations, and agreements.

Amendments: No amendment is binding unless made in writing and signed by both parties.

Severability: If any provision is unenforceable, the remaining provisions continue in full force.

Waiver: Failure to enforce any provision does not waive future enforcement rights.

Notices: Notices must be in writing and delivered by email (with acknowledgement), registered mail, or hand delivery to the addresses in Section 1.

Electronic Execution: This agreement may be signed in counterparts; electronic signatures (PDF, DocuSign, or image) are as valid as original wet signatures.`,

  schedule_time:
`Time is of the essence. The Contractor shall commence work on the Start Date and achieve Substantial Completion by the date specified in the Project Details.

The Contractor shall immediately notify OchrePoint in writing of any anticipated delay, its cause, and expected duration. Delays attributable to the Contractor do not entitle the Contractor to a time extension or additional compensation.

Owner-directed changes or proven force-majeure events may be grounds for a mutually agreed time extension confirmed in a written Change Order.`,

  independent_contractor:
`The Contractor is an independent contractor — not an employee, agent, partner, or joint venturer of OchrePoint — and has no authority to bind OchrePoint to any obligation.

The Contractor is solely responsible for all source deductions, CPP, EI, and income-tax remittances required by the Income Tax Act and Employment Insurance Act for the Contractor and everyone the Contractor employs or engages.

The Contractor shall not subcontract any portion of the work without prior written consent from OchrePoint.`,

  wcb_ohs:
`The Contractor must hold a valid WCB account in good standing before commencing work and must maintain it throughout. A current Clearance Letter must be on file with OchrePoint prior to start.

The Contractor shall maintain a valid WCB account covering all workers on this project. A current Clearance Letter must be provided before work begins and upon request.

The Contractor shall comply with the Workers Compensation Act, the OHS Regulation, site-specific safety plans, and OchrePoint's site rules; conduct regular toolbox talks; and maintain a site safety program.

All incidents, near-misses, and injuries must be reported to OchrePoint's site supervisor immediately and to WCB as required by law.

OchrePoint may stop work without penalty if the Contractor is in violation of safety requirements. Costs of the work stoppage are borne by the Contractor.

WHMIS compliance is required for all hazardous materials; Safety Data Sheets (SDS) must be on site at all times.`,

  insurance_requirements:
`Certificates of insurance naming OchrePoint Contracting LTD as additional insured must be delivered before work commences.

Commercial General Liability (CGL): Minimum $2,000,000 per occurrence / $2,000,000 aggregate, with OchrePoint Contracting LTD named as an additional insured.

Automobile Liability: Minimum $2,000,000 covering all owned, leased, or non-owned vehicles used in connection with the work.

Tools & Equipment: The Contractor is responsible for insuring its own tools, equipment, and materials on site. OchrePoint bears no liability for theft, loss, or damage to the Contractor's property.

If the Contractor fails to maintain required insurance, OchrePoint may obtain such coverage and deduct the premium from amounts owing.`,
};

// ── Standard Work Agreement defaults ─────────────────────────────────────────

export const DEFAULT_CLAUSES: AgreementClauses = {
  change_orders:           { enabled: true,  content: CLAUSE_TEXT.change_orders },
  warranty:                { enabled: true,  content: CLAUSE_TEXT.warranty },
  lien_rights:             { enabled: true,  content: CLAUSE_TEXT.lien_rights },
  confidentiality:         { enabled: true,  content: CLAUSE_TEXT.confidentiality },
  termination:             { enabled: true,  content: CLAUSE_TEXT.termination },
  dispute:                 { enabled: true,  content: CLAUSE_TEXT.dispute },
  general_conditions:      { enabled: true,  content: CLAUSE_TEXT.general_conditions },
  schedule_time:           { enabled: false, content: CLAUSE_TEXT.schedule_time },
  independent_contractor:  { enabled: false, content: CLAUSE_TEXT.independent_contractor },
  wcb_ohs:                 { enabled: false, content: CLAUSE_TEXT.wcb_ohs },
  insurance_requirements:  { enabled: false, content: CLAUSE_TEXT.insurance_requirements },
  additional:              { enabled: false, content: '' },
};

// ── Subcontractor Work Order defaults (all clauses on) ────────────────────────

export const DEFAULT_WO_CLAUSES: AgreementClauses = {
  schedule_time:           { enabled: true, content: CLAUSE_TEXT.schedule_time },
  change_orders:           { enabled: true, content: CLAUSE_TEXT.change_orders },
  independent_contractor:  { enabled: true, content: CLAUSE_TEXT.independent_contractor },
  wcb_ohs:                 { enabled: true, content: CLAUSE_TEXT.wcb_ohs },
  insurance_requirements:  { enabled: true, content: CLAUSE_TEXT.insurance_requirements },
  warranty:                { enabled: true, content: CLAUSE_TEXT.warranty },
  lien_rights:             { enabled: true, content: CLAUSE_TEXT.lien_rights },
  confidentiality:         { enabled: true, content: CLAUSE_TEXT.confidentiality },
  termination:             { enabled: true, content: CLAUSE_TEXT.termination },
  dispute:                 { enabled: true, content: CLAUSE_TEXT.dispute },
  general_conditions:      { enabled: true, content: CLAUSE_TEXT.general_conditions },
  additional:              { enabled: false, content: '' },
};

export const DEFAULT_MATERIALS_SUPPLY: MaterialsSupply = {
  labour_only: false,
  materials_by_principal: false,
  materials_by_subcontractor: false,
  equipment_by_principal: false,
  equipment_by_subcontractor: false,
};

export type AgreementType = 'work_agreement' | 'subcontractor_work_order';

export interface Agreement {
  id: number;
  agreement_number: string;
  client_id: number;
  job_id: number | null;
  title: string;
  status: 'draft' | 'sent' | 'signed' | 'cancelled';
  agreement_type: AgreementType;
  // Client
  client_name: string;
  client_address: string;
  client_email: string;
  client_phone: string;
  // Contractor (OchrePoint)
  company_name: string;
  company_address: string;
  company_email: string;
  company_phone: string;
  company_gst: string;
  company_bc_inc: string;
  // Principal contractor (when OchrePoint is subcontractor)
  principal_name: string;
  principal_bc_inc: string;
  principal_gst: string;
  principal_address: string;
  principal_rep: string;
  principal_phone: string;
  principal_email: string;
  // WCB
  wcb_account: string;
  wcb_classification: string;
  wcb_effective_date: string;
  // Insurance
  insurance_insurer: string;
  insurance_policy: string;
  insurance_cgl_limit: string;
  // Site
  site_city: string;
  site_postal_code: string;
  // Scope
  scope_of_work: string;
  scope_exclusions: string;
  // Payment
  contract_value: number | null;
  deposit_percent: number;
  payment_schedule: 'on-completion' | 'milestone' | 'progress' | 'time_and_materials';
  payment_notes: string;
  hourly_rate: number | null;
  nte_cap: number | null;
  payment_due_days: number;
  holdback_percent: number;
  payment_milestones: PaymentMilestone[] | null;
  materials_supply: MaterialsSupply | null;
  // Timeline
  start_date: string;
  estimated_end_date: string;
  // Clauses / notes
  clauses: AgreementClauses | null;
  internal_notes: string;
  agreement_date: string;
  signed_at: string | null;
  signed_by_name: string | null;
  sent_at: string | null;
  created_at: string;
  // Joined
  client_display_name?: string;
  job_title?: string;
  job_number?: string;
}

export const listAgreements = async (params?: { status?: string; client_id?: string; job_id?: string }): Promise<Agreement[]> => {
  const res = await apiClient.get('/agreements', { params });
  return res.data.data;
};

export const getAgreement = async (id: string): Promise<Agreement> => {
  const res = await apiClient.get(`/agreements/${id}`);
  return res.data.data;
};

export const createAgreement = async (data: Partial<Agreement>): Promise<{ id: number; agreement_number: string }> => {
  const res = await apiClient.post('/agreements', data);
  return res.data.data;
};

export const updateAgreement = async (id: number, data: Partial<Agreement>): Promise<void> => {
  await apiClient.put(`/agreements/${id}`, data);
};

export const deleteAgreement = async (id: number): Promise<void> => {
  await apiClient.delete(`/agreements/${id}`);
};

export const sendAgreementForSigning = async (id: number): Promise<{
  signingUrl: string;
  expiresAt: string;
  clientEmail: string | null;
  emailSent: boolean;
}> => {
  const res = await apiClient.post(`/agreements/${id}/send`);
  return res.data.data;
};

export const getAgreementBySignToken = async (token: string): Promise<Agreement> => {
  const res = await apiClient.get(`/agreements/sign/${token}`);
  return res.data.data;
};

export const signAgreementByToken = async (token: string, name: string): Promise<void> => {
  await apiClient.post(`/agreements/sign/${token}`, { name });
};

export const generateScopeOfWork = async (params: {
  title: string;
  client_name?: string;
  site_address?: string;
  agreement_type?: string;
  notes?: string;
}): Promise<string> => {
  const res = await apiClient.post('/ai/scope-of-work', params);
  return res.data.data;
};
